import { useQuery } from '@apollo/client';
import { ApexOptions } from 'apexcharts';
import { t } from 'i18next';
import moment from 'moment';
import { useEffect } from 'react';
import ApexCharts from 'react-apexcharts';
import { useErrorHandler } from 'react-error-boundary';
import { toast } from 'react-toastify';
import { CHART_COLORS_HEX } from '../../../constants/colors';
import { useThemeContext } from '../../../context/Theme';
import { LIST_EVENTS } from '../../../http/apollo-queries';
import { EventsList } from '../../Strategies/types';
import { numberFormat } from '../../../helpers/calc';
import { CurrencyCodeEnum } from '../../../constants/enums';
import { TInterval } from '../../../hooks/useIntervals';

// [Timestamp, O, H, L, C]
export type TApexChartSeriesType = 'candlestick' | 'line';
export type TApexChartSeries = {
  name: string;
  type: TApexChartSeriesType;
  data: {
    x: number;
    y: number | number[];
  }[];
  rawData: number[][];
  rawPercentage: number[][];
};

const colors = Object.values(CHART_COLORS_HEX);

export const ApexChart = ({
  canShowBlackSwan,
  canShowPriceTargets,
  isLoading,
  series = [],
  currency = CurrencyCodeEnum.USD,
  chartType = 'line',
  targets,
  precision = 2,
  setBlackSwanToggleState,
  interval,
}: {
  canShowBlackSwan: boolean;
  canShowPriceTargets: boolean;
  series: TApexChartSeries[];
  currency?: string;
  chartType: TApexChartSeriesType;
  isLoading: boolean;
  precision?: number;
  targets: { y: number; colorIdx: number; name: string }[];
  setBlackSwanToggleState(newState?: boolean | undefined): void;
  interval: TInterval;
}) => {
  const { theme } = useThemeContext();
  const onError = useErrorHandler();

  const suffix = series.length === 1 ? ' ' + currency : '%';

  const { data: { listEvents: { items: events = [] } = {} } = {} } =
    useQuery<EventsList>(LIST_EVENTS, { onError });

  const { startDate, endDate } = series.reduce(
    ({ startDate, endDate }, { data }) => {
      data.forEach(({ x }) => {
        startDate > x && (startDate = x);
        endDate < x && (endDate = x);
      });

      return { startDate, endDate };
    },
    { startDate: Infinity, endDate: -Infinity }
  );

  // * BlackSwan
  const xaxis = canShowBlackSwan
    ? events
        .filter(
          ({ startDate: date }) =>
            new Date(date).getTime() >= startDate &&
            new Date(date).getTime() <= endDate
        )
        .map(({ startDate: date, title }) => ({
          x: new Date(date).getTime(),
          borderColor: '#ff0c1f',
          label: {
            borderColor: '#dfe72a',
            style: {
              fontSize: '12px',
              color: '#000',
              background: '#dfe72a',
            },
            orientation: 'horizontal',
            offsetY: -10,
            text: title,
          },
        }))
    : [];

  useEffect(() => {
    if (canShowBlackSwan && xaxis.length === 0) {
      toast.warn(t('strategies.warnings.blackSwan'));
      setBlackSwanToggleState?.(false);
    }
  }, [canShowBlackSwan]);

  // * Target prices
  const yaxis = canShowPriceTargets
    ? targets.map(({ colorIdx, name, y }, i) => ({
        y,
        offsetY: -8,
        borderColor: colors[colorIdx],
        label: {
          borderColor: i % 2 ? '#FF2B00' : '#35CB00',
          style: {
            color: '#fff',
            background: i % 2 ? '#FF2B00' : '#35CB00',
          },
          text: `${name.length > 7 ? name.slice(0, 8) + '...' : name} stop ${
            i % 2 ? 'loss' : 'profit'
          } on ${y}${series.length === 1 ? '$' : '%'}`,
        },
      }))
    : [];

  // const min = Math.min(
  //   ...series.map(({ data }) => data.map(({ y }) => y)).flat(2),
  //   ...yaxis.map(({ y }) => y)
  // );
  //
  // const max = Math.max(
  //   ...series.map(({ data }) => data.map(({ y }) => y)).flat(2),
  //   ...yaxis.map(({ y }) => y)
  // );

  const tooltipCandleStick: ApexOptions['tooltip'] = {
    shared: false,
    intersect: true,
    custom({ dataPointIndex }) {
      // logger.log('ApexChart:tooltip', {
      //   series,
      //   valueSeries,
      //   seriesIndex,
      //   dataPointIndex,
      //   w,
      //   colors,
      // });

      const dateHtml = `<div class="flex items-center uppercase text-grey-50 ml-3">
        ${moment(series[0].data?.[dataPointIndex]?.x).format(
          'dddd, YYYY-MM-DD'
        )}</div>`;

      const profitLossHtml = (n: number) => {
        const color = n > 0 ? 'text-positive' : 'text-negative';
        return `<span class="text-sm ${color}">${n || 0} % </span>`;
      };

      const assetsHtml = series
        .map(
          (s, index) =>
            `<div class="flex items-center gap-1 border-l-3 pl-2 my-2" style="border-color:${
              colors[index]
            }">
                <div class="text-grey-70 dark:text-white mr-2">
                  <span class="font-semibold">${s.name}: </span>
                  <div>
                    <span class="text-xs font-semibold">Open:</span>
                    ${numberFormat(+s.rawData?.[dataPointIndex]?.[0])}
                    ${currency}
                    ${profitLossHtml(s.rawPercentage?.[dataPointIndex]?.[0])}
                  </div>
                  <div>
                    <span class="text-xs font-semibold">High:</span>
                    ${numberFormat(+s.rawData?.[dataPointIndex]?.[1])}
                    ${currency}
                    ${profitLossHtml(s.rawPercentage?.[dataPointIndex]?.[1])}
                  </div>
                  <div>
                    <span class="text-xs font-semibold">Low:</span>
                    ${numberFormat(+s.rawData?.[dataPointIndex]?.[2])}
                    ${currency}
                    ${profitLossHtml(s.rawPercentage?.[dataPointIndex]?.[2])}
                  </div>
                  <div>
                    <span class="text-xs font-semibold">Close:</span>
                    ${numberFormat(+s.rawData?.[dataPointIndex][3])}
                    ${currency}
                    ${profitLossHtml(s.rawPercentage?.[dataPointIndex]?.[3])}
                  </div>
                
              </div>
            </div>`
        )
        .join('');

      return `
          <div class="bg-white dark:bg-grey-90 dark:border-grey-80 p-4 border border-grey-10 rounded-lg shadow-lg">
            ${dateHtml}
            ${assetsHtml}
          </div>
    `;
    },
  };

  const tooltipLine: ApexOptions['tooltip'] = {
    shared: true,
    intersect: false,
    custom({ dataPointIndex }) {
      // logger.log('ApexChart:tooltip', {
      //   series,
      //   valueSeries,
      //   seriesIndex,
      //   dataPointIndex,
      //   w,
      //   colors,
      // });

      const dateHtml = `<div class="flex items-center uppercase text-grey-50 ml-3">
        ${moment(series[0].data?.[dataPointIndex]?.x).format(
          'dddd, YYYY-MM-DD'
        )}</div>`;

      const profitLossHtml = (n: number) => {
        const color = n > 0 ? 'text-positive' : 'text-negative';
        return `<span class="text-sm ${color}">${n || 0} % </span>`;
      };

      const assetsHtml = series
        .map(
          (s, index) =>
            `<div class="flex items-center gap-1 border-l-3 pl-2 my-2" style="border-color:${
              colors[index]
            }">
                <div class="text-grey-70 dark:text-white mr-2">
                  <span class="font-semibold">${s.name}: </span>
                  <span>
                      ${numberFormat(+s.rawData?.[dataPointIndex]?.[0])}
                      ${currency}
                  </span>
                  ${profitLossHtml(s.rawPercentage?.[dataPointIndex]?.[0])}
                  </span>
              </div>
            </div>`
        )
        .join('');

      return `
          <div class="bg-white dark:bg-grey-90 dark:border-grey-80 p-4 border border-grey-10 rounded-lg shadow-lg">
            
            ${dateHtml}
            ${assetsHtml}
          
          </div>
    `;
    },
  };

  const options: ApexOptions = {
    annotations: { xaxis, yaxis },
    chart: {
      animations: { enabled: false },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'IBM Plex Sans, sans-serif',
      background: theme === 'DARK' ? '#1c1c1e' : '#fff',
      foreColor: theme === 'DARK' ? '#fff' : '#1c1c1e',
    },
    colors,
    legend: {
      itemMargin: { vertical: 13 },
    },
    markers: {
      size: 0,
      colors,
    },
    grid: {
      show: true,
      borderColor: theme === 'DARK' ? '#595959' : '#eaeaea',
    },
    tooltip: chartType === 'candlestick' ? tooltipCandleStick : tooltipLine,
    plotOptions: {
      candlestick: {
        colors: {
          upward: '#35CB00',
          downward: '#FF2B00',
        },
      },
    },

    stroke: {
      curve: 'smooth',
      width: 1.5,
      colors,
    },
    xaxis: {
      tooltip: { enabled: false },
      type: series.length > 1 ? 'datetime' : 'category',
      labels: {
        rotateAlways: true,
        formatter: (val) => {
          if (interval === 'VAR_24H' && series[0].data.length > 2) {
            return moment(+val).format('HH:mm');
          } else {
            return moment(+val).format('YY MMM DD');
          }
        },
      },
    },
    yaxis: {
      tooltip: { enabled: false },
      labels: {
        formatter: (val) =>
          (val?.toFixed(series.length === 1 ? precision : 0) ?? '') + suffix,
      },
    },
  };

  // logger.log('ApexChart', series, options);

  return (
    <>
      {!isLoading && (
        <ApexCharts
          height={'100%'}
          options={options}
          type={'candlestick'}
          series={series.map((s) => {
            return {
              ...s,
              data: s.data.map((d) => {
                return {
                  ...d,
                  x: series.length > 1 ? d.x : d.x.toString(),
                };
              }),
            };
          })}
        />
      )}
    </>
  );
};
